import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface OutlineContent {
  id: string;
  name: string;
  content_type: string | null;
  duration: string | null;
  access_level: string;
}

export interface OutlineChapter {
  id: string;
  name: string;
  week_number: number | null;
  contents: OutlineContent[];
}

/** Temario (capítulos + contenidos) de una ruta, solo informativo. */
export function useSelfPacedRouteOutline(programId?: string, enabled = true) {
  return useQuery({
    queryKey: ['self-paced-outline', programId],
    enabled: !!programId && enabled,
    queryFn: async (): Promise<OutlineChapter[]> => {
      if (!programId) return [];

      const { data: chapters, error: chErr } = await supabase
        .from('program_chapters')
        .select('id, name, week_number, sort_order')
        .eq('program_id', programId)
        .order('sort_order', { ascending: true });
      if (chErr) throw chErr;
      if (!chapters?.length) return [];

      const { data: links, error: lkErr } = await supabase
        .from('chapter_contents')
        .select('chapter_id, content_id, sort_order')
        .in('chapter_id', chapters.map(c => c.id))
        .order('sort_order', { ascending: true });
      if (lkErr) throw lkErr;

      const contentIds = Array.from(new Set((links ?? []).map(l => l.content_id)));
      let contentMap = new Map<string, OutlineContent>();

      if (contentIds.length) {
        // Vista pública segura: permite a visitantes ver los títulos del temario
        // sin exponer URLs ni datos internos de content_bank.
        const { data: contents, error: cErr } = await supabase
          .from('public_content_catalog')
          .select('id, name, content_type, duration, access_level')
          .in('id', contentIds);
        if (cErr) throw cErr;
        contentMap = new Map((contents ?? []).map(c => [c.id, c as OutlineContent]));
      }

      return chapters.map(ch => ({
        id: ch.id,
        name: ch.name,
        week_number: ch.week_number,
        contents: (links ?? [])
          .filter(l => l.chapter_id === ch.id)
          .map(l => contentMap.get(l.content_id))
          .filter(Boolean) as OutlineContent[],
      }));
    },
  });
}
